import { useEffect, useRef } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import type { AnyAction } from "redux";
import type { ThunkDispatch } from "redux-thunk";
import * as actions from "../../store/actions/index";

import { convertToTwoDecimals, withRouter } from "../../shared/utility";

import ItemsScreen from "../../components/forms/cart/ItemsScreen";
import OrderSummary from "../../components/forms/cart/OrderSummary";
import { CartViewModel } from "../../viewmodels/cart/CartViewModel";
import type { AuthState } from "../../types/auth";
import type { CartItem, CartOrderDetails, CartState } from "../../types/orders";

interface CartProps {
  cart: CartItem[];
  orderDetails: CartOrderDetails | null;
  setCart: (cart: CartItem[]) => void;
  setOrderDetails: (details: CartOrderDetails | null) => void;
  logout: () => void;
}
interface StoreState { auth: AuthState; cart: CartState }

function Cart(props: CartProps) {
  const { cart, setCart, orderDetails, setOrderDetails, logout } = props;

  const { createOrderFromCart, revalidateCart } = CartViewModel(
    setCart,
    setOrderDetails,
    logout
  );

  const navigate = useNavigate();
  const hasRevalidatedPersistedCart = useRef(false);

  useEffect(() => {
    if (hasRevalidatedPersistedCart.current || !orderDetails?.customer_id || cart.length === 0) return;
    hasRevalidatedPersistedCart.current = true;
    void revalidateCart(cart, orderDetails);
  }, [cart, orderDetails, revalidateCart]);

  const onHandleInputChange = (name: keyof CartItem, incomingValue: number | string, index: number) => {
    const copyProducts = [...cart];
    const singleItem = copyProducts[index];
    if (!singleItem) return;
    let newValue: number | string = incomingValue;

    // Cap discount at 0 and at item price so discount cannot exceed price
    if (name === "discount") {
      const priceNum = Number(singleItem.price) || 0;
      let discountNum = Number(newValue);
      if (Number.isNaN(discountNum)) discountNum = 0;
      newValue = Math.max(0, Math.min(priceNum, discountNum));
    }

    let sub_dollar_amount = Number(singleItem.sub_dollar_amount);
    if (name === "price") {
      sub_dollar_amount = (Number(newValue) - singleItem.discount) * singleItem.quantity;
    } else if (name === "quantity") {
      sub_dollar_amount = Number(newValue) * (singleItem.price - singleItem.discount);
    } else if (name === "discount") {
      sub_dollar_amount = (singleItem.price - Number(newValue)) * singleItem.quantity;
    }

    const taxRate = Number(name === "tax_rate" ? newValue : (singleItem.tax_rate ?? 0));
    const total_dollar_amount = Number(convertToTwoDecimals(sub_dollar_amount * (1 + taxRate / 100)));

    const singleItemBeingEdited = {
      ...singleItem,
      [name]: newValue,
      sub_dollar_amount: sub_dollar_amount,
      total_dollar_amount: total_dollar_amount,
    };
    const newProducts = copyProducts.map((item, itemIndex) => itemIndex === index ? singleItemBeingEdited : item);
    if (name === "quantity" && newValue === 0) {
      newProducts.splice(index, 1);
    }
    setCart(newProducts);
  };

  const onRemoveProductFromCart = (_product: CartItem, index: number) => {
    const newProducts = cart.filter((_item, itemIndex) => itemIndex !== index);
    setCart(newProducts);
  };

  function clearCart() {
    setOrderDetails(null);

    setCart([]);
  }

  return (
    <div className="h-100 bg-main">
      {cart.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <h2
            className="text-xl font-semibold apollo-heading mb-2"
            style={{ color: "var(--heading-primary)" }}
          >
            Your cart is empty
          </h2>
          <p className="apollo-body mb-6" style={{ color: "var(--nav-text-secondary)" }}>
            Create an order to add items.
          </p>
          <button
            type="button"
            onClick={() => navigate("/create-order")}
            className="inline-flex items-center justify-center px-6 py-3 font-medium rounded-lg text-white transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--nav-active-accent)] hover:opacity-90 bg-[var(--nav-active-accent)]"
          >
            Create order
          </button>
        </div>
      ) : (
        <div className="border-t-2 grid grid-cols-5 divide-x divide-gray-200">
          <div className="px-8 py-4 col-span-3 space-y-4">
            {cart.length > 0 ? (
              <div className="flex">
                <p className="text-xl font-semibold text-gray-900">
                  Cart for: {orderDetails?.company_name ?? "Selected customer"}
                </p>
                <button
                  type="submit"
                  className="ml-auto justify-center rounded-md bg-indigo-500 px-3 py-2 text-lg font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                  onClick={clearCart}
                >
                  Clear cart
                </button>
              </div>
            ) : null}

            <ItemsScreen
              cart={cart}
              onRemoveProductFromCart={onRemoveProductFromCart}
              onHandleInputChange={onHandleInputChange}
            />
          </div>

          {orderDetails ? <OrderSummary
            orderDetails={orderDetails}
            cart={cart}
            setOrderDetails={setOrderDetails}
            errors={{}}
            createOrderFromCart={createOrderFromCart}
          /> : null}
        </div>
      )}
    </div>
  );
}

const mapStateToProps = (state: StoreState) => {
  return {
    cart: state.cart.cart ?? [],
    orderDetails: state.cart.orderDetails,
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch<StoreState, unknown, AnyAction>) => {
  return {
    setCart: (newCart: CartItem[]) => dispatch(actions.setCart(newCart)),
    setOrderDetails: (customer: CartOrderDetails | null) => dispatch(actions.setOrderDetails(customer)),
    logout: () => dispatch(actions.logout()),
  };
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Cart));
